import CloseIcon from '@material-ui/icons/Close';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import { Dialog } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download.svg';
import { ReactComponent as FileDocumentIcon } from '../../../../assets/icons/file-document.svg';
import { useAppDispatch } from '../../../../hooks';
import { getFileViewUrl } from '../../../../reducers/work';
import { downloadFile } from '../../../../services/utils';
import { isImageType, isVideoType } from '../../../../utils/fileTypeHelpers';
import * as S from './styled';

interface AttachmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    type: string;
    name: string;
    fileId: number;
    currentIndex?: number;
    totalItems?: number;
    onNext?: () => void;
    /** Override the default download behaviour (which calls the backend attachment service).
     *  Use this when the attachment was uploaded via a different service (e.g. waggle). */
    onDownload?: () => Promise<void>;
}

const AttachmentPopup: React.FC<AttachmentPopupProps> = ({
    isOpen,
    onClose,
    url,
    type,
    name,
    fileId,
    currentIndex = 1,
    totalItems = 1,
    onNext,
    onDownload
}) => {
    const dispatch = useAppDispatch();
    const isImage = isImageType(type);
    const isVideo = isVideoType(type);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [ isPlaying, setIsPlaying ] = useState(false);
    const [ progress, setProgress ] = useState(0);
    const [ currentTime, setCurrentTime ] = useState('00:00');
    const [ duration, setDuration ] = useState('00:00');

    const formatTime = (timeInSeconds: number): string => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const currentTimeValue = videoRef.current.currentTime;
            const durationValue = videoRef.current.duration || 0;
            setProgress((currentTimeValue / durationValue) * 100);
            setCurrentTime(formatTime(currentTimeValue));
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(formatTime(videoRef.current.duration));
        }
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleProgressChange = (
        _event: Event,
        newValue: number | number[]
    ) => {
        if (videoRef.current && typeof newValue === 'number') {
            const newTime = (videoRef.current.duration * newValue) / 100;
            videoRef.current.currentTime = newTime;
            setProgress(newValue);
            setCurrentTime(formatTime(newTime));
        }
    };

    const handleRewind = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.max(
                0,
                videoRef.current.currentTime - 2
            );
        }
    };

    const handleForward = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.min(
                videoRef.current.duration,
                videoRef.current.currentTime + 2
            );
        }
    };

    const handleNextAttachment = () => {
        if (onNext) {
            onNext();
        }
    };

    const handleDownload = async () => {
        try {
            if (onDownload) {
                await onDownload();
            } else {
                const response = await dispatch(getFileViewUrl({ fileId })).unwrap();
                await downloadFile(response.url, name || 'download');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime('00:00');
        setDuration('00:00');
    }, [ url ]);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth={false}
            PaperComponent={S.StyledDialogPaper}
        >
            <S.HeaderBox>
                <S.HeaderTitle variant="subtitle1">
                    Attachment
                </S.HeaderTitle>
                <S.CounterText variant="h6">
                    {totalItems > 0 ? `${currentIndex}/${totalItems}` : '1/1'}
                </S.CounterText>

                <S.CloseButton
                    aria-label="close"
                    onClick={onClose}
                >
                    <CloseIcon />
                </S.CloseButton>
            </S.HeaderBox>
            <S.FileInfoBox>
                <S.FileName variant="subtitle1">
                    {name}
                </S.FileName>

                <S.DownloadButton
                    aria-label="download"
                    onClick={handleDownload}
                >
                    <DownloadIcon width={20} height={24} />
                </S.DownloadButton>
            </S.FileInfoBox>

            <S.StyledDialogContent>
                {isImage ? (
                    <S.ImageContainer>
                        <S.StyledImage
                            src={url}
                            alt={name}
                        />
                    </S.ImageContainer>
                ) : isVideo ? (
                    <S.VideoContainer>
                        <S.VideoWrapper>
                            <S.StyledVideo
                                ref={videoRef}
                                src={url}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onClick={togglePlay}
                            />
                            {!isPlaying && (
                                <S.PlayButton onClick={togglePlay}>
                                    <PlayArrowIcon style={S.PlayIconStyles} />
                                </S.PlayButton>
                            )}
                        </S.VideoWrapper>

                        <S.VideoControlsBox>
                            <S.StyledSlider
                                value={progress}
                                onChange={handleProgressChange}
                            />

                            <S.ControlsContainer>
                                <S.PlaybackControls>
                                    <S.ControlButton
                                        size="small"
                                        onClick={handleRewind}
                                    >
                                        <RotateLeftIcon />
                                    </S.ControlButton>

                                    <S.ControlButtonWithMargin
                                        size="small"
                                        onClick={togglePlay}
                                    >
                                        {isPlaying ? (
                                            <PauseIcon />
                                        ) : (
                                            <PlayArrowIcon />
                                        )}
                                    </S.ControlButtonWithMargin>

                                    <S.ControlButton
                                        size="small"
                                        onClick={handleForward}
                                    >
                                        <RotateRightIcon />
                                    </S.ControlButton>
                                </S.PlaybackControls>

                                <S.TimeText variant="body2">
                                    {`${currentTime} / ${duration}`}
                                </S.TimeText>
                            </S.ControlsContainer>
                        </S.VideoControlsBox>
                    </S.VideoContainer>
                ) : (
                    <S.NoPreviewContainer>
                        <FileDocumentIcon width={80} height={80} />
                        <S.NoPreviewTitle variant="h6">
                            Preview not available for this file type
                        </S.NoPreviewTitle>
                        <S.NoPreviewText variant="body2">
                            Click the download icon to view this file
                        </S.NoPreviewText>
                    </S.NoPreviewContainer>
                )}
            </S.StyledDialogContent>

            {totalItems > 1 && currentIndex < totalItems && (
                <S.ButtonContainer>
                    <S.ActionButton onClick={handleNextAttachment}>
                        <S.ButtonText variant="button">
                            Next
                        </S.ButtonText>
                    </S.ActionButton>
                </S.ButtonContainer>
            )}

            {currentIndex === totalItems && (
                <S.ButtonContainer>
                    <S.ActionButton onClick={onClose}>
                        <S.ButtonText variant="button">
                            Close
                        </S.ButtonText>
                    </S.ActionButton>
                </S.ButtonContainer>
            )}
        </Dialog>
    );
};

export default AttachmentPopup;
